import React from "react";
import FollowButton from "@/components/follow/FollowButton";
import RankBadge from "@/components/RankBadge";
import { db } from "@/lib/firebase";
import { doc, onSnapshot } from "firebase/firestore";

type ListingContext = {
  docId?: string | null;
  ownerUid?: string | null;
  name?: string | null;
  scryfall_id?: string | null;
};

type Props = {
  targetUid: string;
  username: string;
  className?: string;
  onMessage?: () => void;
  onViewProfile?: () => void;
  onMakeOffer?: () => void; // If provided (listings), opens card details modal
  listingContext?: ListingContext;
  extra?: React.ReactNode;
};

export default function ProfileInlineMenu({
  targetUid,
  username,
  className,
  onMessage,
  onViewProfile,
  onMakeOffer,
  listingContext,
  extra,
}: Props) {
  const [rankPoints, setRankPoints] = React.useState<number>(0);
  const [rankTitle, setRankTitle] = React.useState<string>("Initiate");
  const [copied, setCopied] = React.useState(false);

  // Subscribe to the user's public rank fields
  React.useEffect(() => {
    if (!targetUid) return;
    const ref = doc(db, "users", targetUid);
    const off = onSnapshot(
      ref,
      (snap) => {
        const d = (snap.data() as any) || {};
        const pts =
          typeof d?.rank_points_total === "number"
            ? d.rank_points_total
            : typeof d?.total_score === "number"
            ? d.total_score
            : 0;
        const ttl = typeof d?.rank_title === "string" ? d.rank_title : "Initiate";
        setRankPoints(pts);
        setRankTitle(ttl);
      },
      () => {
        // ignore errors; keep defaults
      },
    );
    return () => off();
  }, [targetUid]);

  // Helper: open card details modal (listings only)
  function openCardDetailsFromMenu() {
    if (typeof onMakeOffer === "function") {
      onMakeOffer();
      return;
    }
    if (listingContext?.docId) {
      window.dispatchEvent(
        new CustomEvent("carddetails:open", {
          detail: {
            docId: listingContext.docId,
            ownerUid: listingContext.ownerUid ?? targetUid ?? null,
            name: listingContext.name ?? null,
            scryfall_id: listingContext.scryfall_id ?? null,
          },
        }),
      );
    }
  }

  async function copyProfileLink() {
    const path = `/${username}`;
    const url =
      typeof window !== "undefined" && window.location?.origin
        ? `${window.location.origin}${path}`
        : path;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // no-op
    }
  }

  return (
    <div
      className={`mt-2 rounded-lg border border-slate-700 bg-gray-900 text-white shadow-2xl relative isolate z-[2147483647] ${className || ""}`}
      style={{
        // Try to keep within parent/card horizontally while floating above outlines
        maxWidth: "min(92vw, calc(100% - 12px))",
        // Ensure it sits on top within any stacking context
        // Some parents with transforms/filters can create new contexts.
        // isolate + huge z-index keeps it above sibling overlays.
        willChange: "transform",
      }}
      role="menu"
      data-top-layer="inline-menu"
    >
      {/* 1) RankBadge header */}
      <div className="px-3 py-2 border-b border-slate-800">
        <RankBadge title={rankTitle} points={rankPoints} size="small" showPoints />
      </div>

      {/* 2) Make an offer (only for card listings) */}
      {listingContext?.docId ? (
        <button
          className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm"
          onClick={openCardDetailsFromMenu}
          data-market-interactive="1"
          role="menuitem"
        >
          Make an offer
        </button>
      ) : null}

      {/* 3) Message */}
      {onMessage && (
        <button
          className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm"
          onClick={onMessage}
          role="menuitem"
        >
          Message
        </button>
      )}

      {/* 4) View profile */}
      {onViewProfile && (
        <button
          className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm"
          onClick={onViewProfile}
          role="menuitem"
        >
          View profile
        </button>
      )}

      {/* 5) Share profile (copy link) */}
      <button
        className="w-full text-left px-3 py-2 hover:bg-white/5 text-sm inline-flex items-center gap-2"
        onClick={copyProfileLink}
        role="menuitem"
        title={`Copy link to @${username}`}
      >
        <span>Share profile</span>
        <span className="ml-auto text-[11px] opacity-70">{copied ? "Copied!" : `@${username}`}</span>
      </button>

      {/* Optional slot (e.g., “See this seller only”) */}
      {extra}

      {/* 6) Follow/Following */}
      <div className="px-3 py-2 follow-menu">
        <style>{`
          .follow-menu button {
            width: 100%;
            text-align: left;
            padding: 0.5rem 0.75rem;
            border-radius: 0.375rem;
            border: 1px solid transparent;
            background: transparent;
            color: inherit;
            font-size: 0.875rem;
          }
          .follow-menu button:hover { background: rgba(255,255,255,0.05); }
          .follow-menu button[aria-pressed="false"],
          .follow-menu button[data-following="false"] {
            background: #5b1fae !important;
            color: #fbbf24 !important;
            border-color: rgba(125, 61, 246, 0.6) !important;
            box-shadow: 0 0 0 0 rgba(125, 61, 246, 0.3);
          }
          .follow-menu button[aria-pressed="true"],
          .follow-menu button[data-following="true"] {
            background: transparent !important;
            color: inherit !important;
            border-color: transparent !important;
          }
        `}</style>
        <FollowButton
          targetUid={targetUid}
          username={username}
          appearance="menu"
          className="w-full text-left px-3 py-2 text-sm hover:bg-white/5"
        />
      </div>
    </div>
  );
}
